import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageLayout from "@/components/feature/PageLayout";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutInfo } from "@/mocks/videoData";

export default function AboutPage() {
  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "苹果视频",
    url: "https://www.apple-video.com",
    description: aboutInfo.introduction,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: aboutInfo.contact.phone,
      contactType: "customer service",
      email: aboutInfo.contact.email,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: aboutInfo.contact.address,
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
    sameAs: [
      "https://weibo.com/applevideo",
    ],
  };

  return (
    <PageLayout
      seo={
        <SEOHead
          title="关于我们 - 苹果视频"
          description={`${aboutInfo.brandName} - ${aboutInfo.slogan}。${aboutInfo.introduction.substring(0, 100)}`}
          keywords="苹果视频,关于我们,免费视频平台,高清流畅,品牌介绍,联系方式"
          canonicalUrl="/about"
        />
      }
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <Navbar />

      <div className="pt-20">
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20modern%20office%20with%20warm%20ambient%20lighting%2C%20minimalist%20design%2C%20soft%20cream%20and%20terracotta%20tones%2C%20spacious%20collaborative%20workspace%2C%20professional%20aesthetic&width=1920&height=800&seq=about-hero-bg&orientation=landscape"
            alt="关于苹果视频"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="苹果视频 - 关于我们"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 w-full">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading">关于我们</h1>
            <p className="text-sm md:text-base text-white/70 mt-3">{aboutInfo.slogan}</p>
          </div>
        </section>

        {/* Brand */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
            <div className="mt-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                品牌介绍
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                {aboutInfo.introduction}
              </p>
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-6 mt-6">
                <p className="text-sm text-primary-800 font-medium leading-relaxed flex items-start gap-2">
                  <i className="ri-double-quotes-l text-primary-400 text-lg flex-shrink-0 mt-0.5"></i>
                  {aboutInfo.mission}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* History */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8">
              发展历程
            </h2>
            <div className="space-y-0">
              {aboutInfo.history.map((item, index) => (
                <div key={index} className="flex gap-4 pb-8 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></div>
                    {index < aboutInfo.history.length - 1 && (
                      <div className="w-0.5 flex-1 bg-primary-200 mt-1.5"></div>
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-bold text-primary-500">{item.year}</span>
                    <p className="text-sm text-foreground-700 mt-1">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 mb-8">听听用户们怎么说</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {aboutInfo.testimonials.map((item, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent-600">{item.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{item.content}</p>
                  <div className="flex gap-0.5 mt-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
              联系方式
            </h2>
            <p className="text-sm text-foreground-600 mb-8">如有任何问题，欢迎通过以下方式联系我们</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-3">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.email}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className="ri-wechat-line text-accent-500 text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.wechat}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-3">
                  <i className="ri-weibo-line text-secondary-500 text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">微博</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.weibo}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-3">
                  <i className="ri-phone-line text-primary-500 text-lg"></i>
                </span>
                <h3 className="text-sm font-medium text-foreground-950 mb-1">客服电话</h3>
                <p className="text-xs text-foreground-600">{aboutInfo.contact.phone}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </PageLayout>
  );
}